// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/message-popup.service.ts
// DATE: 2026-08-09
// PURPOSE: Nimmt die Nachrichten entgegen, die über den WebSocket eintreffen,
//          und zeigt sie EINZELN NACHEINANDER als Popup.
// ceeq.it: Item #78 in _34 · Tasks #307, #310
//
// SO ARBEITET DER DIENST
//   Eintreffende Nachrichten kommen in eine Warteschlange. Sichtbar ist immer
//   genau eine. Erst wenn der Nutzer sie schliesst, rückt die nächste nach.
//
//   Daraus folgt das Verhalten, das gewünscht ist:
//     ▸ Es erscheinen nie mehrere Popups gleichzeitig.
//     ▸ Es läuft nichts von selbst weiter — das Tempo bestimmt der Nutzer.
//     ▸ Jede Nachricht wird genau einmal gezeigt.
//
// DREI SICHERUNGEN GEGEN EINE FLUT
//   1. Entdopplung über den Schlüssel (id, uuid). Dieselbe Nachricht zweimal
//      zugestellt erzeugt ein Popup.
//   2. Ein Gedächtnis der bereits gezeigten Schlüssel für die Dauer der
//      Sitzung. Ein erneuter Verbindungsaufbau wiederholt nichts.
//   3. Eine Obergrenze der Warteschlange. Läuft sie voll, fällt das ÄLTESTE
//      heraus und der Zähler bleibt ehrlich — das Neueste ist das, worauf
//      jemand reagieren würde.
//
// ZWEI ZEITSTEMPEL, BEIDE UTC
//   ereignisUtc   wann der Vorgang war, der die Nachricht ausgelöst hat
//                 (kommt aus der Nachricht selbst)
//   gezeigtUtc    wann das Popup vor Augen kam (wird hier gesetzt, in dem
//                 Moment, in dem die Nachricht sichtbar wird)
//
// VERWANDT: services/message-socket.service.ts (Zulieferer)
//           components/message-popup/message-popup.component.ts (Anzeige)
// ═══════════════════════════════════════════════════════════════════════════

import { Injectable, computed, inject, signal } from '@angular/core';
import { CeeqMessage } from '../types/ceeq-message';
import { MessageSocketService } from './message-socket.service';

/** Eine Nachricht mit dem Zeitpunkt, an dem sie gezeigt wurde. */
export interface PopupEintrag {
  nachricht: CeeqMessage;
  /** ISO-8601 UTC. Gesetzt, sobald der Eintrag sichtbar wird. */
  gezeigtUtc: string;
}

/** Mehr als das hält niemand nach; alles darüber ist Lärm. */
const MAX_WARTESCHLANGE = 20;

@Injectable({ providedIn: 'root' })
export class MessagePopupService {
  private socket = inject(MessageSocketService);

  private warteschlange: CeeqMessage[] = [];
  private gezeigt = new Set<string>();
  private gestartet = false;

  /** Was gerade zu sehen ist. `null` = kein Popup offen. */
  readonly aktuell = signal<PopupEintrag | null>(null);

  /** Wie viele noch warten — für die Zeile „+n weitere". */
  readonly wartend = signal<number>(0);

  /** Für die Anzeige des Verbindungszustands im Popup-Fuss. */
  readonly verbunden = this.socket.connected;

  /**
   * Einmal beim Start der App aufrufen. Mehrfachaufruf ist harmlos.
   * Der Aufruf abonniert den Socket und baut die Verbindung auf.
   */
  starten(): void {
    if (this.gestartet) return;
    this.gestartet = true;

    this.socket.messages$.subscribe((m) => this.annehmen(m));
    this.socket.connect();

    console.log('[MessagePopup] bereit — Popups laufen ab jetzt app-weit');
  }

  /**
   * Der Nutzer hat geschlossen. Die nächste Nachricht rückt nach.
   * Ohne diesen Aufruf bewegt sich nichts — das ist der Sinn.
   */
  schliessen(): void {
    this.aktuell.set(null);
    this.naechsteZeigen();
  }

  /**
   * Nach der Anmeldung erneut verbinden.
   *
   * ▸ WARUM ES DAS BRAUCHT: Wer die App auf der Anmeldeseite öffnet, hat noch
   *   keine Sitzung. Der Handshake wird zu Recht abgelehnt, und der Socket
   *   versucht es bewusst kein zweites Mal. Da die Anmeldung ohne Neuladen der
   *   Seite weitergeht, bliebe die Verbindung sonst für immer aus.
   *
   * ▸ Angestossen wird nur aus den Ruhezuständen. Während `connecting` oder
   *   `retrying` liegt bereits ein Versuch an; ein zweiter erzeugte einen
   *   zweiten Socket.
   */
  sicherstellenVerbunden(): void {
    const zustand = this.socket.state();
    if (zustand === 'idle' || zustand === 'rejected') {
      console.log(`[MessagePopup] Verbindung erneut anstossen (Zustand war "${zustand}")`);
      this.socket.connect();
    }
  }

  /** Alles verwerfen, was noch wartet. Für „nicht jetzt". */
  alleVerwerfen(): void {
    const weg = this.warteschlange.length;
    this.warteschlange = [];
    this.wartend.set(0);
    this.aktuell.set(null);
    console.log(`[MessagePopup] ${weg} wartende Nachrichten verworfen — sie bleiben im Posteingang`);
  }

  // ── Innenleben ───────────────────────────────────────────────────────────

  /** Schlüssel einer Nachricht. `id` allein genügt nicht: die Projektsicht
   *  führt je Empfänger eine Zeile mit derselben `id`. */
  private schluessel(m: CeeqMessage): string {
    return `${m.id ?? 'x'}·${m.uuid ?? 'x'}·${m.utc}`;
  }

  private annehmen(m: CeeqMessage): void {
    const k = this.schluessel(m);

    // ── Leise Stufe: kein Popup ──────────────────────────────────────────
    //   `fyi_logged` heisst „zur Kenntnis". Solche Nachrichten entstehen aus
    //   dem allgemeinen Projektstrom und aus abonnierten Linien der Kategorie
    //   „ich lese mit". Würde jede davon aufpoppen, wäre die App nach einer
    //   Stunde unbenutzbar, und der Nutzer schaltete die Benachrichtigungen
    //   ganz ab — danach erreichte ihn auch das Wichtige nicht mehr.
    //   Im Posteingang steht sie trotzdem, vollständig.
    //   ceeq.it Task #341
    if (m.obligation === 'fyi_logged') {
      console.log(`[MessagePopup] id=${m.id} ist fyi_logged — nur Posteingang, kein Popup`);
      return;
    }

    if (this.gezeigt.has(k)) {
      console.log(`[MessagePopup] id=${m.id} bereits gezeigt — übersprungen`);
      return;
    }
    if (this.warteschlange.some((w) => this.schluessel(w) === k)) {
      console.log(`[MessagePopup] id=${m.id} liegt schon in der Warteschlange`);
      return;
    }

    this.warteschlange.push(m);

    if (this.warteschlange.length > MAX_WARTESCHLANGE) {
      const raus = this.warteschlange.shift();
      console.warn(
        `[MessagePopup] Warteschlange voll (${MAX_WARTESCHLANGE}) — ` +
        `älteste Nachricht id=${raus?.id} fällt heraus. Sie bleibt im Posteingang.`
      );
    }

    this.wartend.set(Math.max(0, this.warteschlange.length - (this.aktuell() ? 0 : 1)));

    // Ist gerade nichts zu sehen, sofort zeigen. Sonst wartet sie.
    if (!this.aktuell()) this.naechsteZeigen();
  }

  private naechsteZeigen(): void {
    const naechste = this.warteschlange.shift();
    if (!naechste) {
      this.wartend.set(0);
      return;
    }

    this.gezeigt.add(this.schluessel(naechste));
    this.aktuell.set({ nachricht: naechste, gezeigtUtc: new Date().toISOString() });
    this.wartend.set(this.warteschlange.length);

    console.log(
      `[MessagePopup] 🔔 zeige id=${naechste.id} ` +
      `obligation=${naechste.obligation} anker=${naechste.anchor?.type}#${naechste.anchor?.id} ` +
      `— noch ${this.warteschlange.length} in der Warteschlange`
    );
  }
}
